import DoppelModal from '@/components/doppel_design/doppel_modal'
import PaginatedTrademarkDropdown from '@/components/enforcements/paginated_trademark_dropdown'
import { HoverThumbnail } from '@/components/shared/hover_thumbnail'
import {
  AutoActionTypeReportTag,
  ReportChangeEventSource,
  ReportStatus,
  TagActionType,
} from '@/generated/enums'
import {
  useGetLatestPredictedEnforcementTypeQuery,
  useHandleAutoActioningAlertSelectionMutation,
  useUpdateSpoofingReportsMutation,
} from '@/generated/graphql'
import { TAG_NAME_TO_ID } from '@/utils/constants'
import { ENFORCEMENT_TYPE_TAGS_BY_PRODUCT } from '@/utils/enforcement_utils'
import { getExternalReportId } from '@/utils/reports/report_utils'
import {
  DOPPEL_BLUE_100,
  DOPPEL_BREACH_RED,
  DOPPEL_CYAN_200,
  DOPPEL_DARK_SECONDARY,
  DOPPEL_WHITE,
} from '@/utils/style'
import { getFlaggedPreviewImage } from '@/utils/web2'
import {
  Box,
  Checkbox,
  Flex,
  HStack,
  Link,
  Radio,
  Spinner,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useToast,
} from '@chakra-ui/react'
import { Key, useEffect, useState } from 'react'
import { NO_TRADEMARK_SELECTED } from './suggested_enforcements_form'

type BulkTagAlertsModalProps = {
  isOpen: boolean
  onClose: () => void
  selectedRows: any[]
  isSingleAlertMode: boolean
  onSuggestedEnforcementsOverride?: () => void
}

export default function BulkTagAlertsModal({
  isOpen,
  onClose,
  selectedRows = [],
  isSingleAlertMode = false,
  onSuggestedEnforcementsOverride,
}: BulkTagAlertsModalProps) {
  const toast = useToast()
  const [selectedTags, setSelectedTags] = useState({})
  const maxAlertsAllowed = 50
  const limitedSelectedRows = selectedRows.slice(0, maxAlertsAllowed)

  const [updateSpoofingReportsMutation] = useUpdateSpoofingReportsMutation()
  const [handleAutoActioningAlertSelectionMutation] =
    useHandleAutoActioningAlertSelectionMutation()

  // Only get prediction data when there's exactly one alert (single alert mode)
  const isSingleAlert = limitedSelectedRows.length === 1
  const firstAlert = isSingleAlert ? limitedSelectedRows[0] || {} : {}
  const firstAlertId = firstAlert?.id || ''
  const firstMatchId = firstAlert?.spoof_matches?.[0]?.id || ''

  // Use a single query with skip logic to avoid invalid hook calls
  // Only fetch prediction if we're in single alert mode
  const { data: predictionData, loading: predictionLoading } =
    useGetLatestPredictedEnforcementTypeQuery({
      variables: {
        alert_id: firstAlertId || '',
        match_id: firstMatchId || '',
      },
      skip: !isOpen || !firstAlertId || !firstMatchId || !isSingleAlert,
    })

  // Pre-populate selected tags based on predicted enforcement types
  // Only apply predictions when there's exactly one alert
  useEffect(() => {
    if (
      isOpen &&
      isSingleAlert &&
      predictionData?.predicted_enforcement_types?.length > 0
    ) {
      const prediction = predictionData.predicted_enforcement_types[0]
      // Only proceed if prediction exists and has the required property
      if (
        prediction &&
        typeof prediction === 'object' &&
        'predicted_enforcement_type' in prediction
      ) {
        const predictedType = prediction.predicted_enforcement_type

        // Only process if we have a valid prediction type
        if (predictedType && typeof predictedType === 'string') {
          // Map the predicted enforcement type to the corresponding tag
          let tagToSelect = null

          // Convert the predicted enforcement type to the available tags in the enum
          switch (predictedType.toUpperCase()) {
            case 'TRADEMARK':
              tagToSelect = AutoActionTypeReportTag.TRADEMARK
              break
            case 'COPYRIGHT':
              tagToSelect = AutoActionTypeReportTag.COPYRIGHT
              break
            case 'PHISHING':
              tagToSelect = AutoActionTypeReportTag.PHISHING
              break
            case 'DMCA':
              tagToSelect = AutoActionTypeReportTag.DMCA
              break
            case 'WALLET_BLOCK':
              tagToSelect = AutoActionTypeReportTag.WALLET_BLOCK
              break
            default:
              // No matching tag found
              break
          }

          if (tagToSelect) {
            // Update the selected tags for the first alert
            setSelectedTags((prev) => {
              const updated = { ...prev }
              const alertId = firstAlertId

              if (!updated[alertId]) {
                updated[alertId] = {}
              }

              // Clear any existing selections
              Object.keys(updated[alertId]).forEach((existingTag) => {
                updated[alertId][existingTag] = {
                  isSelected: false,
                  trademarkId: null,
                }
              })

              // Set the predicted tag
              updated[alertId][tagToSelect] = {
                isSelected: true,
                trademarkId: null,
              }

              return updated
            })
          }
        }
      }
    }
  }, [isOpen, predictionData, firstAlertId])

  const updateSelectedTags = (alertId: string, tag: Key, isSelected: boolean) => {
    setSelectedTags((prev) => {
      const updated = { ...prev }
      if (!updated[alertId]) {
        updated[alertId] = {}
      }

      // For radio buttons, we should clear all tags first
      if (isSelected) {
        Object.keys(updated[alertId]).forEach((existingTag) => {
          updated[alertId][existingTag] = {
            isSelected: false,
            trademarkId: null,
          }
        })
      }

      // Set the current tag
      updated[alertId][tag] = {
        isSelected,
        trademarkId: null,
      }

      return updated
    })
  }

  const handleUpdatingTagsAndSubmit = async () => {
    for (const alert of limitedSelectedRows) {
      const alertId = alert.id
      const product = alert.platform_subtype?.product || alert.platform?.product

      const tags =
        ENFORCEMENT_TYPE_TAGS_BY_PRODUCT[product] ||
        ENFORCEMENT_TYPE_TAGS_BY_PRODUCT.default

      for (const tag of tags) {
        const tagObj = selectedTags[alertId]?.[tag]
        const autoactioning_metadata =
          tagObj?.isSelected && tagObj.trademarkId
            ? {
                trademarkId: tagObj?.trademarkId,
              }
            : {}
        if (tagObj?.isSelected) {
          const tagId = TAG_NAME_TO_ID[tag]
          await updateSpoofingReportsMutation({
            variables: {
              input: {
                report_ids: [alertId],
                update_source: ReportChangeEventSource.BULK_UI,
                report_status: ReportStatus.REPORTED,
                tag_id: tagId,
                tag_action: TagActionType.ADD,
              },
            },
          })
          await handleAutoActioningAlertSelectionMutation({
            variables: {
              handle_auto_actioning_alert_selection_input: {
                alert_id: alertId,
                autoactioning_metadata: autoactioning_metadata,
              },
            },
          })
        }
      }
    }
  }

  const validateSubmission = () => {
    return limitedSelectedRows.every((row) => {
      const alertId = row.id
      const product = row.platform_subtype?.product || row.platform?.product
      const tags =
        ENFORCEMENT_TYPE_TAGS_BY_PRODUCT[product] ||
        ENFORCEMENT_TYPE_TAGS_BY_PRODUCT.default

      const hasSomeTagSelected = tags.some(
        (tag) => selectedTags[alertId]?.[tag]?.isSelected,
      )
      if (!hasSomeTagSelected) {
        return false
      }

      const trademarkTag = selectedTags[alertId]?.[AutoActionTypeReportTag.TRADEMARK]
      if (trademarkTag?.isSelected && !trademarkTag.trademarkId) {
        return false
      }

      return true
    })
  }

  const handleSubmit = async () => {
    if (!validateSubmission()) {
      toast({
        title: 'Submission Error',
        description:
          'All alerts must have at least one tag selected. For trademark tags, please ensure a valid trademark is selected.',
        status: 'error',
        isClosable: true,
      })
      return
    }
    try {
      await handleUpdatingTagsAndSubmit()
      toast({
        title: 'Alerts updated successfully',
        status: 'success',
        isClosable: true,
      })
      onClose()
    } catch (error) {
      toast({
        title: 'Error tagging alerts',
        description: error.message,
        status: 'error',
        isClosable: true,
      })
    }
  }

  const bulkTagModalDescription = () => {
    if (isSingleAlertMode) {
      return (
        <>
          <Text color={DOPPEL_WHITE} fontSize="sm">
            Please select the appropriate tags for this alert before proceeding with
            enforcement.
          </Text>

          <Text fontSize="sm" fontWeight="bold" mt={2}>
            Note: The selected tags will be used to determine the enforcement action.
          </Text>
        </>
      )
    }

    // Get tag information to determine if there's only one tag type
    const type =
      limitedSelectedRows[0]?.platform_subtype?.product ||
      limitedSelectedRows[0]?.platform?.product
    const tagsByAlertType = !(type in ENFORCEMENT_TYPE_TAGS_BY_PRODUCT)
      ? ENFORCEMENT_TYPE_TAGS_BY_PRODUCT.default
      : ENFORCEMENT_TYPE_TAGS_BY_PRODUCT[type]
    const tagColumns = Object.values(tagsByAlertType) as Key[]
    const hasSingleTagType = tagColumns.length === 1
    const singleTagType = hasSingleTagType ? tagColumns[0] : null

    // Don't show the "Select All" option for Trademark tags as they require additional info
    const showSelectAll =
      hasSingleTagType && singleTagType !== AutoActionTypeReportTag.TRADEMARK

    // Determine if all alerts have the single tag selected
    const areAllSelected =
      showSelectAll &&
      limitedSelectedRows.every(
        (row) => selectedTags[row.id]?.[singleTagType]?.isSelected === true,
      )

    // Handle select/deselect all checkbox change
    const handleSelectAllChange = (isChecked: boolean) => {
      if (!singleTagType) return

      setSelectedTags((prev) => {
        const updated = { ...prev }
        limitedSelectedRows.forEach((row) => {
          const alertId = row.id
          if (!updated[alertId]) {
            updated[alertId] = {}
          }

          if (isChecked) {
            Object.keys(updated[alertId]).forEach((existingTag) => {
              updated[alertId][existingTag] = {
                isSelected: false,
                trademarkId: null,
              }
            })

            updated[alertId][singleTagType] = {
              isSelected: true,
              trademarkId: null,
            }
          } else {
            updated[alertId][singleTagType] = {
              isSelected: false,
              trademarkId: null,
            }
          }
        })
        return updated
      })
    }

    return (
      <>
        <Text color={DOPPEL_BREACH_RED} fontSize="sm">
          This modal allows tagging and auto-actioning on selected alerts. Ensure that
          all alerts have the correct tags and should be actioned.
        </Text>

        <Text color={DOPPEL_BREACH_RED} fontSize="sm" fontWeight="bold">
          Warning: Clicking Submit will tag and auto-action all selected alerts. Please
          double-check that the tags are correct before proceeding.
        </Text>

        {/* Add Select All checkbox below warning message when there's only one non-trademark tag type */}
        {showSelectAll && (
          <Flex alignItems="center" mt={2}>
            <Checkbox
              isChecked={areAllSelected}
              onChange={(e) => handleSelectAllChange(e.target.checked)}
              size="sm"
            >
              <Text fontSize="sm" ml={1}>
                {areAllSelected ? 'Untag' : 'Tag'} all alerts as{' '}
                {formatTag(singleTagType)}
              </Text>
            </Checkbox>
          </Flex>
        )}
      </>
    )
  }

  const formatTag = (tagString) => {
    return tagString.replace(/_/g, ' ')
  }

  const updateTrademark = (
    alertId: string,
    trademarkId: string,
    trademarkMark: string,
  ) => {
    setSelectedTags((prev) => {
      const updated = { ...prev }
      if (!updated[alertId]) {
        updated[alertId] = {}
      }
      updated[alertId][AutoActionTypeReportTag.TRADEMARK] = {
        isSelected: true,
        trademarkId,
        trademarkMark,
      }
      return updated
    })
  }

  const renderModalBody = () => {
    const type =
      limitedSelectedRows[0]?.platform_subtype?.product ||
      limitedSelectedRows[0]?.platform?.product
    const tagsByAlertType = !(type in ENFORCEMENT_TYPE_TAGS_BY_PRODUCT)
      ? ENFORCEMENT_TYPE_TAGS_BY_PRODUCT.default
      : ENFORCEMENT_TYPE_TAGS_BY_PRODUCT[type]
    const tagColumns = Object.values(tagsByAlertType) as Key[]

    return (
      <Stack spacing="24px">
        <Stack spacing="8px">
          {!isSingleAlertMode ? (
            <Text fontSize="md" fontWeight="bold">
              Number of alerts selected: {limitedSelectedRows.length}
            </Text>
          ) : null}

          {bulkTagModalDescription()}
        </Stack>

        <div
          style={{
            maxHeight: '400px',
            overflowY: 'auto',
          }}
        >
          <Table size="md" variant="simple">
            <Thead
              style={{
                position: 'sticky',
                top: 0,
                zIndex: 1,
                backgroundColor: DOPPEL_DARK_SECONDARY,
              }}
            >
              <Tr>
                <Th>External ID</Th>

                <Th>URL</Th>

                {tagColumns.map((tag) => (
                  <Th key={tag} textAlign="center">
                    {formatTag(tag)}
                  </Th>
                ))}
              </Tr>
            </Thead>

            <Tbody>
              {limitedSelectedRows.map((row) => {
                const alertId = row.id
                const matchId = row.spoof_matches?.[0]?.id
                const alertExternalId = getExternalReportId(
                  row.organization.abbr_name,
                  row.external_id,
                )
                const alertUrl = row.flagged_url
                const thumbnailUrl = getFlaggedPreviewImage(row)

                // Only show prediction data when there's exactly one alert
                const isPredictionForThisAlert =
                  isSingleAlert &&
                  alertId &&
                  firstAlertId &&
                  matchId &&
                  firstMatchId &&
                  alertId === firstAlertId &&
                  matchId === firstMatchId
                const loading = isPredictionForThisAlert
                  ? predictionLoading || false
                  : false
                const data = isPredictionForThisAlert ? predictionData || null : null

                return (
                  <Tr key={alertId}>
                    <Td whiteSpace="nowrap">
                      <HoverThumbnail
                        alertId={alertExternalId}
                        screenshotUrl={thumbnailUrl}
                      />
                    </Td>

                    <Td wordBreak="break-word">
                      <Link
                        color={DOPPEL_BLUE_100}
                        href={`https://app.doppel.com/alerts/${alertExternalId}`}
                        isExternal
                      >
                        {alertUrl}
                      </Link>

                      {loading && <Spinner ml={2} size="xs" />}

                      {data?.predicted_enforcement_types?.length > 0 &&
                        data.predicted_enforcement_types[0]
                          ?.predicted_enforcement_type && (
                          <Tooltip
                            hasArrow
                            label={`AI predicted ${
                              data.predicted_enforcement_types[0]
                                ?.predicted_enforcement_type || ''
                            } based on: ${
                              data.predicted_enforcement_types[0]
                                ?.llm_decision_reason || 'analysis'
                            }`}
                            placement="top"
                          >
                            <Text
                              color={DOPPEL_CYAN_200}
                              display="inline"
                              fontSize="xs"
                              ml={2}
                            >
                              (AI Predicted)
                            </Text>
                          </Tooltip>
                        )}
                    </Td>

                    {tagColumns.map((tag) => {
                      const tagIsChecked = selectedTags[alertId]?.[tag]?.isSelected

                      // Determine if this tag is the AI-predicted one
                      let isPredictedTag = false

                      // Only check for predictions in single alert mode
                      if (
                        isPredictionForThisAlert &&
                        data?.predicted_enforcement_types?.length > 0
                      ) {
                        const prediction = data.predicted_enforcement_types[0]

                        if (
                          prediction &&
                          typeof prediction === 'object' &&
                          'predicted_enforcement_type' in prediction
                        ) {
                          const predictedType = prediction.predicted_enforcement_type

                          if (predictedType && typeof predictedType === 'string') {
                            // Use the same logic as in the useEffect hook
                            switch (predictedType.toUpperCase()) {
                              case 'TRADEMARK':
                                isPredictedTag =
                                  tag === AutoActionTypeReportTag.TRADEMARK
                                break
                              case 'COPYRIGHT':
                                isPredictedTag =
                                  tag === AutoActionTypeReportTag.COPYRIGHT
                                break
                              case 'PHISHING':
                                isPredictedTag =
                                  tag === AutoActionTypeReportTag.PHISHING
                                break
                              case 'DMCA':
                                isPredictedTag = tag === AutoActionTypeReportTag.DMCA
                                break
                              case 'WALLET_BLOCK':
                                isPredictedTag =
                                  tag === AutoActionTypeReportTag.WALLET_BLOCK
                                break
                            }
                          }
                        }
                      }

                      return (
                        <Td key={tag}>
                          <HStack justifyContent="center" spacing="2">
                            {/* Create a fixed-height container to ensure consistent vertical alignment */}
                            <Flex
                              alignItems="center"
                              direction="column"
                              height="60px" // Increased height to accommodate the AI indicator
                              justifyContent="flex-start"
                              position="relative" // For absolute positioning of children
                            >
                              {/* Radio button with highlight */}
                              <Flex
                                alignItems="center"
                                justifyContent="center"
                                position="relative"
                              >
                                {/* Add a highlight background for AI prediction */}
                                {isPredictedTag && (
                                  <Box
                                    bg={`${DOPPEL_CYAN_200}20`} // 20% opacity
                                    borderRadius="full"
                                    height="32px"
                                    position="absolute"
                                    width="32px"
                                    zIndex="0"
                                  />
                                )}

                                <Radio
                                  isChecked={tagIsChecked}
                                  name={`tag-${alertId}`}
                                  onChange={(e) =>
                                    updateSelectedTags(alertId, tag, true)
                                  }
                                  zIndex="1"
                                  // Add a cyan border around the predicted option
                                  sx={{
                                    '.chakra-radio__control': {
                                      borderColor: isPredictedTag
                                        ? DOPPEL_CYAN_200
                                        : undefined,
                                      borderWidth: isPredictedTag ? '2px' : undefined,
                                      _checked: {
                                        borderColor: isPredictedTag
                                          ? DOPPEL_CYAN_200
                                          : undefined,
                                        backgroundColor: isPredictedTag
                                          ? DOPPEL_CYAN_200
                                          : undefined,
                                      },
                                    },
                                  }}
                                />
                              </Flex>

                              {/* Add an AI indicator below the predicted option */}
                              {isPredictedTag && (
                                <Text
                                  alignItems="center"
                                  bg={`${DOPPEL_CYAN_200}15`} // 15% opacity background
                                  borderRadius="sm"
                                  color={DOPPEL_CYAN_200}
                                  display="flex"
                                  fontSize="xs"
                                  fontWeight="bold"
                                  gap={0.5}
                                  mt={2.5} // Increased spacing from radio button
                                  position="absolute"
                                  px={1.5}
                                  py={0.5}
                                  top="24px" // Position it below the radio button
                                >
                                  <span>AI</span>

                                  <span> ✓</span>
                                </Text>
                              )}
                            </Flex>

                            {/* Conditionally render the trademark dropdown if 'trademark' is checked */}
                            {tag === AutoActionTypeReportTag.TRADEMARK &&
                              tagIsChecked && (
                                <PaginatedTrademarkDropdown
                                  emptyOptionLabel={NO_TRADEMARK_SELECTED}
                                  onChange={(trademarkId, trademarkMark) =>
                                    updateTrademark(alertId, trademarkId, trademarkMark)
                                  }
                                  pageSize={10}
                                  placeholder="Select Trademark"
                                  selectedTrademarkId={
                                    selectedTags[alertId]?.[tag]?.trademarkId || ''
                                  }
                                  selectedTrademarkMark={
                                    selectedTags[alertId]?.[tag]?.trademarkMark || ''
                                  }
                                  spoofingEntityId={row.original_entity_id}
                                  width="260px"
                                />
                              )}
                          </HStack>
                        </Td>
                      )
                    })}
                  </Tr>
                )
              })}
            </Tbody>
          </Table>
        </div>
      </Stack>
    )
  }

  return (
    <DoppelModal
      body={renderModalBody()}
      isAsync={true}
      isOpen={isOpen}
      onClose={onClose}
      primaryAction={handleSubmit}
      primaryLabel="Submit"
      size="6xl"
      title={
        <Flex alignItems="center" justifyContent="space-between" width="100%">
          <Text>
            {isSingleAlertMode
              ? 'Tag Alert Before Actioning'
              : 'Bulk Tag Alerts Before Actioning'}
          </Text>

          {onSuggestedEnforcementsOverride && (
            <Tooltip label="Warning: This will override the recommended tagging and auto-actioning flow">
              <Link
                color={DOPPEL_CYAN_200}
                fontSize="xs"
                onClick={onSuggestedEnforcementsOverride}
                textDecoration="underline"
              >
                Skip Tagging & Use Suggested Enforcements Flow
              </Link>
            </Tooltip>
          )}
        </Flex>
      }
    />
  )
}
